import { useState } from "react";
import { newDetailId } from "../../lib/connectionDetails";

function blankDraft(fields) {
  return Object.fromEntries(
    fields.map((field) => [field.key, field.type === "checkbox" ? false : ""])
  );
}

function DetailForm({ fields, draft, onChange, onCancel, onSubmit, submitLabel, onDelete }) {
  const required = fields.find((field) => field.required);
  const canSubmit = !required || String(draft[required.key] || "").trim();

  return (
    <form
      className="entry-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      {fields.map((field) => {
        const id = `entry-field-${field.key}`;
        if (field.type === "checkbox") {
          return (
            <label key={field.key} className="entry-check">
              <input
                type="checkbox"
                checked={Boolean(draft[field.key])}
                onChange={(event) => onChange(field.key, event.target.checked)}
              />
              {field.label}
            </label>
          );
        }
        const Field = field.type === "textarea" ? "textarea" : "input";
        return (
          <div key={field.key} className={`entry-field is-${field.width || "full"}`}>
            <label htmlFor={id}>{field.label}</label>
            <Field
              id={id}
              type={field.type === "textarea" ? undefined : field.type || "text"}
              value={draft[field.key] || ""}
              placeholder={field.placeholder || ""}
              onChange={(event) => onChange(field.key, event.target.value)}
              autoFocus={field === fields[0]}
            />
          </div>
        );
      })}
      <div className="entry-form-actions">
        {onDelete && (
          <button type="button" className="link-button is-danger" onClick={onDelete}>
            Delete
          </button>
        )}
        <span className="entry-form-spacer" />
        <button type="button" className="link-button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary btn-sm" disabled={!canSubmit}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

/**
 * One section of the dossier: a titled block of ledger rows, where the meta
 * (a date, a span of years, "Current") sits in a fixed left column so the
 * whole page scans as one timeline. Moments, Career, Education and Locations
 * are all this component with a different field spec.
 */
export default function DetailSection({
  listKey,
  title,
  fields,
  items = [],
  renderItem,
  renderMeta,
  renderSub,
  addLabel,
  emptyCopy,
  onChange,
  disabled = false,
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(() => blankDraft(fields));

  const updateDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const startAdd = () => {
    setEditingId(null);
    setDraft(blankDraft(fields));
    setAdding(true);
  };

  const startEdit = (item) => {
    setAdding(false);
    setDraft({ ...blankDraft(fields), ...item });
    setEditingId(item.id);
  };

  const close = () => {
    setAdding(false);
    setEditingId(null);
  };

  const commitAdd = () => {
    onChange?.([...items, { ...draft, id: newDetailId(listKey) }]);
    close();
  };

  const commitEdit = () => {
    onChange?.(items.map((item) => (item.id === editingId ? { ...item, ...draft } : item)));
    close();
  };

  const removeItem = (id) => {
    onChange?.(items.filter((item) => item.id !== id));
    close();
  };

  return (
    <section className="dossier-section">
      <div className="dossier-head">
        <h2>{title}</h2>
        <span className="dossier-count">{items.length || ""}</span>
      </div>

      {items.length === 0 && !adding && <p className="dossier-empty">{emptyCopy}</p>}

      <ul className="entry-list">
        {items.map((item) =>
          editingId === item.id ? (
            <li key={item.id} className="entry is-editing">
              <DetailForm
                fields={fields}
                draft={draft}
                onChange={updateDraft}
                onCancel={close}
                onSubmit={commitEdit}
                onDelete={() => removeItem(item.id)}
                submitLabel="Save"
              />
            </li>
          ) : (
            <li key={item.id} className="entry">
              <button
                type="button"
                className="entry-row"
                onClick={() => !disabled && startEdit(item)}
                aria-label={`Edit ${renderItem(item)}`}
              >
                <span className="entry-meta">{renderMeta ? renderMeta(item) : ""}</span>
                <span className="entry-title">{renderItem(item)}</span>
                {renderSub && renderSub(item) ? (
                  <span className="entry-note">{renderSub(item)}</span>
                ) : null}
              </button>
            </li>
          )
        )}
      </ul>

      {adding ? (
        <DetailForm
          fields={fields}
          draft={draft}
          onChange={updateDraft}
          onCancel={close}
          onSubmit={commitAdd}
          submitLabel="Add"
        />
      ) : (
        <button type="button" className="link-button dossier-add" onClick={startAdd} disabled={disabled}>
          {addLabel}
        </button>
      )}
    </section>
  );
}
